/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import { connect, ConnectedProps } from 'react-redux';
import { NavigateFunction } from 'react-router-dom';

import FileUploadDialog from './FileUploadDialog';
import TrialsList from './TrialsList';
import LoopbackProvider from '../../loopback/LoopbackProvider';
import downloadAsExcel, { downloadExcelSheets } from '../utilities/ExcelExport';
import importExcel from '../utilities/ExcelImport';
import ApiClient from '../../loopback/apiClient';
import EditTrialDialog from './EditTrialDialog';

import {
  showErrorNotification,
  showNotification,
  requestTrialSuccess,
} from '../../redux/actions';
import {
  RootState,
  ThirdPartyTrial,
  Trial,
  TrialTagMapping,
  ZerpaState,
  TrialCategoryValue,
} from '../../redux/types';
import { DefaultDataType as ComboboxDataType } from '../layout/ComboBox';

import {
  MANUALS_CONTAINER_NAME,
  DELIVERY_LIST_CONTAINER_NAME,
  getInvoiceDownloadUrl,
  TERMS_CONTAINER_NAME,
} from '../../environment';
import { ZerpaTableStateSetters } from '../layout/table/ZerpaTable';
import Prompt from '../layout/Prompt';

type TrialExtended = Trial & {
  thirdPartyTrial?: ThirdPartyTrial,
  trialTagMapping: TrialTagMapping[];
}

type PropTypes = {
  trials: TrialExtended[];
  updateTrial: (trial: Trial) => Promise<unknown>;
  createCertificates: (trial: Trial) => void;
  editGroundTruth: (trial: Trial) => void;
  reloadTrials: () => void;
  importDeliveryList: (trialId: number, data: unknown) => void;
  importDeliveryListV2: (trialId: number, data: unknown) => void;
  openTrialDetails: (trialId: number) => void;
  goToSurvey: (trial: Trial) => void;
  onCreateBookingsList: (trial: Trial) => Promise<unknown>;
  createTrial: (emptyTrial: Partial<Trial>) => void;
  goToReviewManagement: (trial: Trial) => void;
  downloadInvoicesZip: (trial: Trial) => Promise<unknown>;
  showDeactivatedTrials: boolean;
  onZerpaTableStateChange: (state: ZerpaState, setters?: ZerpaTableStateSetters) => void;
  selectedTab?: 'institute' | 'individual';
  navigate: NavigateFunction;
  setLoading: (spinner: boolean) => void;
} & ReduxType;

type StateTypes = {
  editTrial: Trial | null;
  uploadContainer?: string,
  showUploadForDialogTrial: Trial | null,
  uploadType?: string,
  titleId?: string,
  certRegenerationPrompt?: {
    show: boolean,
    trial: Trial,
  },
}

class TrialManagement extends Component<PropTypes, StateTypes> {
  constructor(props: PropTypes) {
    super(props);
    this.state = {
      editTrial: null,
      showUploadForDialogTrial: null,
    };
    this.onCreateCertificates = this.onCreateCertificates.bind(this);
  }

  onConfirmCertRegeneration = (prompt: boolean) => {
    const { certRegenerationPrompt } = this.state;
    const { trial } = certRegenerationPrompt || {};
    if (prompt && trial) {
      this.onCreateCertificates(trial);
    } else {
      this.setState({ certRegenerationPrompt: undefined });
    }
  };

  onCreateCertificates(trial: Trial) {
    if (trial.manualUploadCert) {
      const { certRegenerationPrompt: promptFlags } = this.state;
      if (!promptFlags?.show) {
        this.setState({ certRegenerationPrompt: { show: true, trial } });
        return;
      } if (promptFlags.show) {
        this.setState({ certRegenerationPrompt: undefined });
        // process will continue further
      }
    }
    const {
      showErrorNotification: showErrorNotificationAction,
      createCertificates,
    } = this.props;
    ApiClient.hasApprovedCertificate(trial.id).then(({ result }) => {
      if (result) {
        showErrorNotificationAction(<FormattedMessage id="trialmanagement.notifyCancelCertificate" />);
        return;
      }

      const { version = 1 } = trial;
      // old survey
      if (version === 1) {
        ApiClient.getTrialWithSurveyAndGroundTruth(trial.id)
          .then((availableTrial) => {
            if ((
              availableTrial.survey
              && availableTrial.survey.groundTruth
              && availableTrial.survey.groundTruth.length
            ) || availableTrial.thirdPartyTrial) {
              createCertificates(trial);
            } else {
              showErrorNotificationAction(<FormattedMessage id="trialmanagement.surveyUnavailable" />);
            }
          });
        return;
      }

      createCertificates(trial);
    }).catch((e) => showErrorNotification(e.message));
  }

  upload = async (trial: Trial | null, input: HTMLInputElement | null) => {
    const {
      updateTrial,
      importDeliveryList,
      importDeliveryListV2,
      reloadTrials,
      showNotification: showNotificationAction,
      showErrorNotification: showErrorNotificationAction,
    } = this.props;
    const { uploadType } = this.state;
    const file = input && input.files && input.files[0];
    if (!file || !trial) {
      return;
    }
    const renamedFile = new File([file], `${trial.id}-${file.name}`);
    const filename = renamedFile.name;
    const uploadFunc = async (container: string) => {
      const formData = new FormData();
      formData.append('file', renamedFile);
      return ApiClient.uploadFile(container, formData);
    };

    switch (uploadType) {
      case 'manual': {
        if (trial) {
          if (trial.manual) {
            try {
              await ApiClient.deleteFile(MANUALS_CONTAINER_NAME, trial.manual);
            } catch (e) {
              // expected catch if file does not exist on filesystem to delete
            }
          }
          await uploadFunc(MANUALS_CONTAINER_NAME);
          await updateTrial({ ...trial, manual: filename });
        }
        break;
      }
      case 'terms': {
        if (trial) {
          if (trial.terms) {
            try {
              await ApiClient.deleteFile(TERMS_CONTAINER_NAME, trial.terms);
            } catch (e) {
              // expected catch if file does not exist on filesystem to delete
            }
          }
          await uploadFunc(TERMS_CONTAINER_NAME);
          await updateTrial({ ...trial, terms: filename });
        }
        break;
      }
      case 'deliveryExcel':
        if (!trial) {
          return;
        }
        try {
          const { id, version = 1 } = trial;
          const data = await importExcel(file);
          // check for version. if version is 2, use new importDeliveryList
          if (version === 2) {
            await importDeliveryListV2(id, data);
          } else {
            await importDeliveryList(id, data);
          }
          showNotificationAction(<FormattedMessage id="feedback.headline" />);
        } catch (e: any) {
          const { message, data } = e;
          showErrorNotificationAction(<FormattedMessage id={message} values={data} />);
        }
        break;
      default:
        break;
    }
    reloadTrials();
    this.closeDialog();
  };

  closeDialog = () => this.setState({ showUploadForDialogTrial: null });

  onCreateEditSurvey = async (trial: Trial) => {
    const {
      goToOldSurveyEditor,
      goToNewSurveyEditor,
      gotoDigitalTrial: gotoDigitalTrialAction,
      navigate,
    } = this.props;
    if (trial.isDRT) {
      gotoDigitalTrialAction(trial.id, navigate);
      return;
    }

    if (trial.version === 2) {
      goToNewSurveyEditor(trial.id, navigate);
      return;
    }
    goToOldSurveyEditor(trial.id, navigate);
  };

  /**
   *
   * @param {string} type button type
   * @param {Trial} trial selected trial for which the action will be performed
   */
  adminActionsHandler = (type: string, trial: Trial) => {
    const {
      onCreateBookingsList,
      editGroundTruth,
      openTrialDetails,
      goToSurvey,
      goToReviewManagement,
      downloadInvoicesZip,
      goToVoucherManagement,
      setTrialInReduxState,
      goToFeedbackView,
      goToAuswertungSettings,
      navigate,
    } = this.props;
    setTrialInReduxState(trial);
    switch (type) {
      case 'editTrialButton':
        this.setState({ editTrial: trial });
        break;
      case 'createEditSurvey':
        this.onCreateEditSurvey(trial);
        break;
      case 'uploadManual':
        this.openManualUploadDialog(trial);
        break;
      case 'createCertificates':
        this.onCreateCertificates(trial);
        break;
      case 'createBookingList':
        onCreateBookingsList(trial)
          .then((data) => downloadAsExcel(data as string[][], `BK-${trial.code}-${moment().format('DDMMYY')}`));
        break;
      case 'uploadDeliveryList':
        this.openDeliveryListUploadDialog(trial);
        break;
      case 'editGroundTruth':
        editGroundTruth(trial);
        break;
      case 'details':
        openTrialDetails(trial.id);
        break;
      case 'goToSurvey':
        goToSurvey(trial);
        break;
      case 'exportSubmissions':
        ApiClient.exportSubmissions(trial)
          .then((data) => downloadExcelSheets(data, `SM-${trial.code}-${moment().format('DDMMYY')}`));
        break;
      case 'exportEvaluations':
        ApiClient.exportEvaluations(trial)
          .then((data) => downloadExcelSheets(data, `EV-${trial.code}-${moment().format('DDMMYY')}`));
        break;
      case 'reviewManagement':
        goToReviewManagement(trial);
        break;
      case 'downloadInvoices':
        downloadInvoicesZip(trial)
          .then((result) => {
            const url = getInvoiceDownloadUrl(
              (result as any).bundlePath, LoopbackProvider.loopbackToken.id,
            );
            window.open(url, '_blank'); // eslint-disable-line
          });
        break;
      case 'voucherManagement':
        goToVoucherManagement(trial.id, navigate);
        break;
      case 'uploadTerms':
        this.openTermsUploadDialog(trial);
        break;
      case 'feedbackView':
        goToFeedbackView(trial.id, navigate);
        break;
      case 'auswertungSettings':
        goToAuswertungSettings(trial.id, navigate);
        break;
      default:
    }
  };

  openManualUploadDialog(trial: Trial) {
    this.setState({
      // eslint-disable-next-line react/no-unused-state
      uploadContainer: MANUALS_CONTAINER_NAME,
      showUploadForDialogTrial: trial,
      uploadType: 'manual',
      titleId: 'trialmanagement.uploadManual',
    });
  }

  openTermsUploadDialog(trial: Trial) {
    this.setState({
      // eslint-disable-next-line react/no-unused-state
      uploadContainer: TERMS_CONTAINER_NAME,
      showUploadForDialogTrial: trial,
      uploadType: 'terms',
      titleId: 'trialmanagement.uploadManual',
    });
  }

  openDeliveryListUploadDialog(trial: Trial) {
    this.setState({
      // eslint-disable-next-line react/no-unused-state
      uploadContainer: DELIVERY_LIST_CONTAINER_NAME,
      showUploadForDialogTrial: trial,
      uploadType: 'deliveryExcel',
      titleId: 'trialmanagement.uploadDeliveryListButton',
    });
  }

  renderTrialActions = (
    trial: TrialExtended,
  ) => {
    const {
      updateTrial,
      reloadTrials,
      createTrial,
      showErrorNotification: showErrorNotificationAction,
      showNotification: showNotificationAction,
      setLoading,
    } = this.props;
    const {
      editTrial,
    } = this.state;
    const { isDRT, version, trialCategory } = trial;
    const isOldSurvey = !version || version < 2;

    return (
      <Box display="flex" flexWrap="wrap" gap={1.5}>
        {
          editTrial
          && (
            <EditTrialDialog
              trial={editTrial}
              isOpen={!!editTrial}
              updateTrial={(editedTrial: Trial, trialTags: ComboboxDataType[] | null) => {
                ApiClient.setTrialTagMapping(editedTrial.id, trialTags || undefined);
                if (editedTrial.reviewCategoryId === 2) {
                  // remote call to check and initiate FaerbungReviewMeta
                  ApiClient.initializeFaerbungMeta(editedTrial.id);
                }
                updateTrial(editedTrial)
                  .then(() => this.setState({ editTrial: null }))
                  .then(reloadTrials)
                  .catch((error: Error) => showErrorNotificationAction(error.message));
              }}
              onClose={() => {
                this.setState({ editTrial: null });
              }}
              onDuplicate={(existing: Trial) => {
                createTrial({
                  ...existing,
                  id: undefined,
                  name: undefined,
                  manual: undefined,
                  enrollments: 0,
                  deliveries: 0,
                  code: undefined,
                  costCenter: undefined,
                  nameOnCertificate: undefined,
                  deactivate: undefined,
                  startDate: moment(existing.startDate).add(1, 'year').toISOString(),
                  endDate: moment(existing.endDate).add(1, 'year').toISOString(),
                });
                this.setState({ editTrial: null });
              }}
            />
          )
        }
        <Button
          variant="contained"
          onClick={() => this.adminActionsHandler('editTrialButton', trial)}
          color="primary"
        >
          <FormattedMessage id="trialmanagement.editTrialButton" />
        </Button>
        <Button
          disabled={trial.deactivate}
          variant="contained"
          onClick={() => this.adminActionsHandler('createEditSurvey', trial)}
          color="primary"
        >
          <FormattedMessage id="trialmanagement.createEditSurvey" />
        </Button>
        <Button
          variant="contained"
          onClick={() => this.adminActionsHandler('uploadManual', trial)}
          color="primary"
          disabled={trial.deactivate}
        >
          <FormattedMessage id="trialmanagement.uploadManual" />
        </Button>
        <Button
          variant="contained"
          onClick={() => this.adminActionsHandler('createCertificates', trial)}
          disabled={trial.deactivate || isDRT}
          color="primary"
        >
          <FormattedMessage id="trialmanagement.createCertificates" />
        </Button>
        <Button
          variant="contained"
          onClick={() => this.adminActionsHandler('createBookingList', trial)}
          color="primary"
        >
          <FormattedMessage id="trialmanagement.createBookingsList" />
        </Button>
        {
          !isDRT && (
            <Button
              variant="contained"
              onClick={() => this.adminActionsHandler('uploadDeliveryList', trial)}
              color="primary"
              disabled={trial.deactivate}
            >
              <FormattedMessage id="trialmanagement.uploadDeliveryListButton" />
            </Button>
          )
        }
        {
          !isDRT && isOldSurvey && (
            <Button
              variant="contained"
              id={`EditGroundTruth-${trial.code}`}
              onClick={() => this.adminActionsHandler('editGroundTruth', trial)}
              color="primary"
            >
              <FormattedMessage id="trialmanagement.editGroundTruth" />
            </Button>
          )
        }
        <Button
          variant="contained"
          color="primary"
          onClick={() => this.adminActionsHandler('details', trial)}
        >
          <FormattedMessage id="trialmanagement.details" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isDRT}
          onClick={() => this.adminActionsHandler('goToSurvey', trial)}
        >
          <FormattedMessage id="trialmanagement.goToSurvey" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isDRT || !isOldSurvey}
          onClick={() => this.adminActionsHandler('exportSubmissions', trial)}
        >
          <FormattedMessage id="trialmanagement.exportSubmissions" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => this.adminActionsHandler('exportEvaluations', trial)}
        >
          <FormattedMessage id="trialmanagement.exportEvaluations" />
        </Button>
        {
          !isDRT && (
            <Button
              variant="contained"
              color="primary"
              disabled={isDRT}
              onClick={() => this.adminActionsHandler('reviewManagement', trial)}
            >
              <FormattedMessage id="review.reviewManagement" />
            </Button>
          )
        }
        <Button
          variant="contained"
          color="primary"
          onClick={async () => this.adminActionsHandler('downloadInvoices', trial)}
          disabled={trial.deactivate}
        >
          <FormattedMessage id="trialmanagement.downloadInvoices" />
        </Button>
        {/* Disable for now as we're not sure if Quip
        wants to have a feedback for DRT and new trial */}
        <Button
          variant="contained"
          color="primary"
          onClick={() => this.adminActionsHandler('feedbackView', trial)}
          disabled={isDRT}
        >
          <FormattedMessage id="feedback.feedbackView" />
        </Button>
        {
          trialCategory === TrialCategoryValue.voucherTrial && (
            <Button
              variant="contained"
              color="primary"
              disabled={trial.trialCategory !== TrialCategoryValue.voucherTrial}
              onClick={() => this.adminActionsHandler('voucherManagement', trial)}
            >
              <FormattedMessage id="voucher.voucherManagement" />
            </Button>
          )
        }
        <Button
          variant="contained"
          color="primary"
          onClick={() => this.adminActionsHandler('uploadTerms', trial)}
        >
          <FormattedMessage id="trialmanagement.uploadTerms" />
        </Button>
        {
          !isOldSurvey && (
            <Button
              variant="contained"
              onClick={() => {
                setLoading(true);
                ApiClient.evaluateAll(trial.id)
                  .then((e) => showNotificationAction(`Success, ${e.length} processsed`))
                  .catch((e) => showErrorNotificationAction(e.message))
                  .finally(() => setLoading(false));
              }}
            >
              <FormattedMessage id="trialdetails.evaluate" />
            </Button>
          )
        }
        {
          !isOldSurvey && (
            <Button
              variant="contained"
              onClick={() => {
                setLoading(true);
                ApiClient.releaseSubmissionEvaluation(trial.id)
                  .then(() => showNotificationAction('Success'))
                  .catch((e) => showErrorNotificationAction(e.message))
                  .finally(() => setLoading(false));
              }}
            >
              <FormattedMessage id="trialdetails.releaseEvaluation" />
            </Button>
          )
        }
        <Button
          variant="contained"
          onClick={() => this.adminActionsHandler('auswertungSettings', trial)}
        >
          Auswertung Settings
        </Button>
      </Box>
    );
  };

  renderFileUploadDialog = () => {
    const { showUploadForDialogTrial, titleId } = this.state;
    return (
      <FileUploadDialog
        titleId={titleId}
        onClose={this.closeDialog}
        isOpen={!!showUploadForDialogTrial}
        upload={(input) => this.upload(showUploadForDialogTrial, input)}
      />
    );
  };

  render() {
    const {
      trials,
      showDeactivatedTrials,
      onZerpaTableStateChange,
      selectedTab,
    } = this.props;
    const { certRegenerationPrompt: certPrompt } = this.state;
    return (
      <div>
        <TrialsList
          trials={trials}
          renderTrialActions={this.renderTrialActions}
          renderAdditionalRows={(trial: Trial) => ([
            [
              <span key="samplesDelivered">
                <FormattedMessage id="trials.samplesDelivered" />
                :
                {' '}
                {trial.deliveries}
                {' '}
                /
                {' '}
                {trial.enrollments}
              </span>,
            ],
          ])}
          showDeactivatedTrials={showDeactivatedTrials}
          onZerpaTableStateChange={onZerpaTableStateChange}
          selectedTab={selectedTab}
        />
        {this.renderFileUploadDialog()}
        <Prompt
          title={<FormattedMessage id="trialmanagement.createCertificates" />}
          display={!!certPrompt?.show}
          action={this.onConfirmCertRegeneration}
        >
          <FormattedMessage id="certificate.uploadWarning" />
        </Prompt>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  trialCategories: state.trialCategories.categories,
});

const mapDispatchToProps = (dispatch: any) => ({
  goToOldSurveyEditor: (trialId: number, navigate: NavigateFunction) => navigate(`/createeditsurvey/${trialId}`),
  goToNewSurveyEditor: (trialId: number, navigate: NavigateFunction) => navigate(`/trial/${trialId}/survey/editor`),
  gotoDigitalTrial: (trialId: number, navigate: NavigateFunction) => navigate(`/digitaltrial/${trialId}/digitalsurvey/editor`),
  goToVoucherManagement: (trialId: number, navigate: NavigateFunction) => navigate(`/vouchermanagement/${trialId}`),
  goToFeedbackView: (trialId: number, navigate: NavigateFunction) => navigate(`/feedback/${trialId}`),
  goToAuswertungSettings: (trialId: number, navigate: NavigateFunction) => navigate(`/trial/${trialId}/auswertungsettings`),
  setTrialInReduxState: (trial: Trial) => dispatch(requestTrialSuccess(trial)),
  showErrorNotification: (
    notification: string | React.ReactElement,
  ) => dispatch(showErrorNotification(notification)),
  showNotification: (
    notification: string | React.ReactElement,
  ) => dispatch(showNotification(notification)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxType = ConnectedProps<typeof connector>;

export default injectIntl(connector(TrialManagement));
